import React, { useEffect, useState } from "react";
import {
  Box, Card, Tabs, Tab, Typography, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Button, Divider
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";

import EKSClusterOverview from "./EKSClusterOverview";
import EKSLoadBalancer from "./EKSLoadBalancer";
import EKSNamespace from "./EKSNamespace";
import EKSNodes from "./EKSNodes";

import { useAuth } from "../state/AuthContext";
import K8sApiService from "../services/K8sApiService";
import DebugService from "../services/DebugService";
import ClusterSkeleton from "./common/contentloader/ClusterSkeleton";
import LoadBalancerSkeleton from "./common/contentloader/LoadBalancerSkeleton";
import NamespaceSkeleton from "./common/contentloader/NamespaceSkeleton";
import SnackbarNotification, { SNACKBAR_THEME } from "./common/SnackbarNotification";
import ConfirmModal from "./common/ConfirmModal";
import { CONST_SITE_LABEL } from "./common/Helper";
import dayjs from "dayjs";
import { Margin } from "@mui/icons-material";

const shellTheme = createTheme({
  typography: { fontFamily: '"DM Sans", "Segoe UI", sans-serif' },
  shape: { borderRadius: 12 },
});

function TabPanel({ children, value, index }) {
  return value === index ? <Box>{children}</Box> : null;
}

const ConnectK8sCluster = () => {
  const { selectedAccount, selectedPlatform } = useAuth();

  const platform = selectedPlatform === 'AZURE' ? 'aks' : 'eks';

  const [clusterData, setClusterData] = useState(null);
  const [nameSpaceData, setNameSpaceData] = useState(null);
  const [loadBalancerData, setLoadBalancerData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isNameSpaceLoading, setIsNameSpaceLoading] = useState(false);
  const [isLoadBalancerLoading, setIsLoadBalancerLoading] = useState(false);

  const [selectedCluster, setSelectedCluster] = useState(null);
  const [selectedClusterData, setSelectedClusterData] = useState(null);

  const [activeTab, setActiveTab] = useState(0);
  const [namespaceAggregate, setNamespaceAggregate] = useState("Namespace");

  // ── Namespace date range (lifted here — single source of truth for all fetch params) ──
  // Default: start and end of the current month, matching the original fetchNameSpaceData logic.
  // Passed down to EKSNamespace as read-only props.
  // EKSNamespace calls onDateChange(start, end) when the user clicks "Apply" — handled below.
  const [namespaceStartDate, setNamespaceStartDate] = useState(
    dayjs().startOf("month").format("YYYY-MM-DD")
  );
  const [namespaceEndDate, setNamespaceEndDate] = useState(
    dayjs().endOf("month").format("YYYY-MM-DD")
  );

  const [isOpenCostModalOpen, setIsOpenCostModalOpen] = useState(false);
  const [openCostModalRow, setOpenCostModalRow] = useState(null);
  const [openCostUrlValue, setOpenCostUrlValue] = useState("");
  const [showDeleteOpenCostConfirm, setShowDeleteOpenCostConfirm] = useState(false);
  const [deleteOpenCostRow, setDeleteOpenCostRow] = useState(null);

  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const [snackbarStatus, setSnackbarStatus] = useState(null);

  useEffect(() => {
    document.title = `${CONST_SITE_LABEL} | K8s Cluster Connection`;
  }, []);

  const fetchClustersData = async () => {
    setIsLoading(true);
    try {
      const response = await K8sApiService.getClusterData(platform, {
        userAccountId: selectedAccount ?? "",
      });
      if (response) {
        DebugService.log(`ConnectK8sCluster: cluster data (${platform})`, response);
        setClusterData({ data: response?.data });
      }
    } catch (error) {
      DebugService.error(`ConnectK8sCluster: error fetching cluster data for ${platform}`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLoadBalancerData = async (cluster) => {
    if (!cluster?.clusterName || !cluster?.region) return;
    setIsLoadBalancerLoading(true);
    try {
      const response = await K8sApiService.getLoadBalancerData(platform, {
        userAccountId: selectedAccount ?? "",
        clusterName: cluster.clusterName,
        region: cluster.region,
        ...(platform === 'aks' && cluster.resourceGroup && { resourceGroup: cluster.resourceGroup }),
      });
      if (response) {
        setLoadBalancerData({ data: response?.data ?? [] });
      }
    } catch (error) {
      DebugService.error(`ConnectK8sCluster: error fetching load balancer data for ${platform}`, error);
    } finally {
      setIsLoadBalancerLoading(false);
    }
  };

  /**
   * Fetch namespace / OpenCost data.
   *
   * All three variable fetch params (aggregate, startDate, endDate) are
   * accepted as explicit arguments so callers can pass the latest values
   * directly — avoiding stale-closure issues with React state.
   *
   * Callers:
   *   - handleClusterSelect  → passes current state values on first load
   *   - handleAggregateChange → passes new aggregate + current dates
   *   - handleNamespaceDateChange → passes current aggregate + new dates
   *
   * @param {object} cluster      - The selected cluster object
   * @param {string} aggregate    - Aggregate type (e.g. "Namespace", "Pod")
   * @param {string} startDate    - Start date in YYYY-MM-DD format
   * @param {string} endDate      - End date in YYYY-MM-DD format
   */
  const fetchNameSpaceData = async (cluster, aggregate, startDate, endDate) => {
    if (!cluster?.opencostUrl) return;
    setIsNameSpaceLoading(true);
    try {
      const response = await K8sApiService.getNamespaceData(platform, {
        userAccountId: selectedAccount ?? "",
        startMonthDate: startDate,
        endMonthDate: endDate,
        region: cluster.region,
        clusterName: cluster.clusterName,
        aggregate: aggregate,
        ...(platform === 'aks' && cluster.resourceGroup && { resourceGroup: cluster.resourceGroup }),
      });
      if (response?.data) {
        const dataArray = Array.isArray(response.data) ? response.data : Object.values(response.data);
        setNameSpaceData({ data: dataArray });
      } else {
        setNameSpaceData(null);
      }
    } catch (error) {
      DebugService.error(`ConnectK8sCluster: error fetching namespace data for ${platform}`, error);
    } finally {
      setIsNameSpaceLoading(false);
    }
  };

  useEffect(() => {
    setSelectedCluster(null);
    setSelectedClusterData(null);
    setNameSpaceData(null);
    setLoadBalancerData(null);
    fetchClustersData();
  }, [selectedAccount, platform]);

  const handleClusterSelect = (cluster) => {
    if (selectedCluster === cluster.clusterName) {
      setSelectedCluster(null);
      setSelectedClusterData(null);
      setNameSpaceData(null);
      setLoadBalancerData(null);
      return;
    }

    setSelectedCluster(cluster.clusterName);
    setSelectedClusterData(cluster);
    setActiveTab(0);
    setNameSpaceData(null);
    setLoadBalancerData(null);

    fetchLoadBalancerData(cluster);
    if (cluster.opencostUrl) {
      // Pass current state values explicitly to avoid stale closures
      fetchNameSpaceData(cluster, namespaceAggregate, namespaceStartDate, namespaceEndDate);
    }
  };

  const handleAggregateChange = (newAggregate) => {
    setNamespaceAggregate(newAggregate);
    if (selectedClusterData?.opencostUrl) {
      // Keep current dates, only aggregate changes
      fetchNameSpaceData(selectedClusterData, newAggregate, namespaceStartDate, namespaceEndDate);
    }
  };

  /**
   * Called by EKSNamespace when the user selects new dates and clicks "Apply".
   * Updates parent date state so it stays in sync, then re-fetches namespace data.
   *
   * @param {string} startDate - YYYY-MM-DD
   * @param {string} endDate   - YYYY-MM-DD
   */
  const handleNamespaceDateChange = (startDate, endDate) => {
    setNamespaceStartDate(startDate);
    setNamespaceEndDate(endDate);
    if (selectedClusterData?.opencostUrl) {
      // Keep current aggregate, only dates change
      fetchNameSpaceData(selectedClusterData, namespaceAggregate, startDate, endDate);
    }
  };

  const handleEditOpencostUrl = (clusterRow) => {
    setOpenCostModalRow(clusterRow);
    setOpenCostUrlValue(clusterRow?.opencostUrl || "");
    setIsOpenCostModalOpen(true);
  };

  const handleSaveOpenCostUrl = async () => {
    if (!openCostUrlValue) return;

    setIsLoading(true);
    try {
      const d = await K8sApiService.saveOpenCostUrl(platform, {
        userAccountId: selectedAccount ?? "",
        clusterName: openCostModalRow?.clusterName,
        region: openCostModalRow?.region,
        ...(platform === 'aks' && openCostModalRow?.resourceGroup && { resourceGroup: openCostModalRow.resourceGroup }),
        opencostUrl: openCostUrlValue,
      });
      if (d?.data?.message) {
        setSnackbarStatus("success");
        setSnackbarMessage(d.data.message);
        setTimeout(() => fetchClustersData(), 500);
        setClusterData(null);
        setSelectedCluster(null);
        setSelectedClusterData(null);
      } else {
        setSnackbarStatus("failed");
        setSnackbarMessage("Failed to save OpenCost URL");
      }
    } catch (error) {
      DebugService.error(`ConnectK8sCluster: error saving OpenCost URL for ${platform}`, error);
    } finally {
      setIsOpenCostModalOpen(false);
      setIsLoading(false);
    }
  };

  const handleDeleteOpencostUrl = (clusterRow) => {
    setDeleteOpenCostRow(clusterRow);
    setShowDeleteOpenCostConfirm(true);
  };

  const handleConfirmDeleteOpenCostUrl = (confirmed) => {
    if (!confirmed) {
      setShowDeleteOpenCostConfirm(false);
      setDeleteOpenCostRow(null);
      return;
    }

    const clusterRow = deleteOpenCostRow;
    setShowDeleteOpenCostConfirm(false);
    if (!clusterRow) return;

    setIsLoading(true);
    K8sApiService.deleteOpenCostUrl(platform, clusterRow.opencostUrlMappingId)
      .then(d => {
        if (d?.data) {
          setSnackbarStatus("success");
          setSnackbarMessage(d.data?.message || "OpenCost URL removed successfully");
          setTimeout(() => fetchClustersData(), 500);
          setClusterData(null);
          setSelectedCluster(null);
          setSelectedClusterData(null);
        }
      })
      .catch(error => {
        DebugService.error(`ConnectK8sCluster: error deleting OpenCost URL for ${platform}`, error);
      })
      .finally(() => {
        setIsLoading(false);
        setDeleteOpenCostRow(null);
      });
  };

  const nodesData = selectedClusterData
    ? { clusterName: selectedClusterData.clusterName, nodes: selectedClusterData.nodes || [] }
    : null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <ThemeProvider theme={shellTheme}>
      <Box sx={{ bgcolor: "#f5f7fa", minHeight: "100vh" }}>

        {/* ── Loading ──────────────────────────────────────────────────────── */}
        {isLoading && <ClusterSkeleton />}

        {/* ── Cluster Overview ─────────────────────────────────────────────── */}
        {!isLoading && (
          <>
            <EKSClusterOverview
              clusterData={clusterData}
              onClusterSelect={handleClusterSelect}
              selectedClusterName={selectedCluster}
              onEditOpenCostUrl={handleEditOpencostUrl}
              onDeleteOpenCostUrl={handleDeleteOpencostUrl}
            />


            {/* ── Detail Tabs (shown when a cluster is selected) ────────────────── */}
            {selectedClusterData && (
              <Box sx={{ px: 3, pb: 3 }}>
                <Card sx={{ boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.06)", border: "1px solid #e2e8f0" }}>
                  {/* Tab Bar */}
                  <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <Tabs
                      value={activeTab}
                      onChange={(_, v) => setActiveTab(v)}
                      aria-label="cluster details tabs"
                      sx={{
                        "& .MuiTab-root": {
                          fontFamily: '"DM Sans", "Segoe UI", sans-serif',
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: 14,
                          minWidth: 140,
                          "&:hover": { backgroundColor: "rgba(2, 132, 199, 0.04)" },
                        },
                        "& .MuiTabs-indicator": { height: 3, backgroundColor: "#0284c7" },
                      }}
                    >
                      <Tab label="Load Balancers" />
                      {selectedClusterData?.opencostUrl && <Tab label="Namespaces" />}
                      <Tab label="Nodes" />
                    </Tabs>
                  </Box>

                  <TabPanel value={activeTab} index={0}>
                    {isLoadBalancerLoading
                      ? <LoadBalancerSkeleton />
                      : <EKSLoadBalancer
                          loadBalancerData={loadBalancerData}
                          clusterLabel={selectedClusterData?.clusterName}
                          platform={platform}
                        />}
                  </TabPanel>
                    
                  {selectedClusterData?.opencostUrl && (
                    <TabPanel value={activeTab} index={1}>
                      {isNameSpaceLoading
                        ? <NamespaceSkeleton />
                        : (
                          <EKSNamespace
                            namespaceData={nameSpaceData}
                            aggregate={namespaceAggregate}
                            onAggregateChange={handleAggregateChange}
                            startMonthDate={namespaceStartDate}
                            endMonthDate={namespaceEndDate}
                            onDateChange={handleNamespaceDateChange}
                            clusterLabel={selectedClusterData?.clusterName}
                          />
                        )}
                    </TabPanel>
                  )}

                  <TabPanel value={activeTab} index={selectedClusterData?.opencostUrl ? 2 : 1}>
                    <EKSNodes nodesData={nodesData} />
                  </TabPanel>
                </Card>
              </Box>
            )}
          </>
        )}

        {/* Manage OpenCost URL Modal */}
        <Dialog
          open={isOpenCostModalOpen}
          onClose={() => setIsOpenCostModalOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }
          }}
        >
          <DialogTitle sx={{ px: 3, pt: 3, pb: 1.5 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, fontSize: '1.05rem', color: 'text.primary', lineHeight: 1.3 }}
            >
              Manage OpenCost URL
            </Typography>
            {openCostModalRow?.clusterName && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                    flexShrink: 0,
                  }}
                />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, letterSpacing: 0.1 }}>
                  {openCostModalRow.clusterName}
                </Typography>
              </Box>
            )}
          </DialogTitle>

          <Divider sx={{ marginBottom: ".5rem" }} />

          <DialogContent sx={{ px: 3, pt: 1, pb: 0 }}>

            {/* Instruction text */}
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.6,
                mb: 2.5,
              }}
            >
              Enter the OpenCost URL for this cluster. This URL is used to pull cost metrics
              directly from your OpenCost deployment.
            </Typography>

            {/* URL Input */}
            <TextField
              label="OpenCost URL"
              fullWidth
              size="small"
              value={openCostUrlValue}
              onChange={e => setOpenCostUrlValue(e.target.value)}
              placeholder={
                platform === 'eks'
                  ? ('http://opencost.your-eks-cluster:9090')
                  : ('http://opencost.your-aks-cluster:9090')
              }
              helperText={`e.g. http://opencost.your-${platform}-cluster:9090`}
              InputProps={{
                sx: {
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                  bgcolor: 'grey.50',
                  '&.Mui-focused': { bgcolor: 'background.paper' },
                }
              }}
              sx={{
                mb: 3,
                '& .MuiFormHelperText-root': {
                  mt: 0.75,
                  color: 'text.disabled',
                  fontSize: '0.76rem',
                },
              }}
            />

            {/* Help / Installation Guide */}
            <Box
              sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  px: 2,
                  py: 1.75,
                  mb: 2.5,
                  bgcolor: '#e6f1fb',
                  border: '1px solid',
                  borderColor: '#b5d4f4',
                  borderRadius: '8px',
              }}
            >
              <Box
                sx={{
                  mt: 0.15,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '6px',
                  bgcolor: '#b5d4f4',
                  flexShrink: 0,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0c447c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </Box>

             <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#042c53', mb: 0.3, fontSize: '0.825rem' }}>
                  Need help setting up OpenCost?
                </Typography>
                <Typography variant="caption" sx={{ color: '#185fa5', display: 'block', mb: 1, lineHeight: 1.5 }}>
                  Follow our step-by-step guide to install and configure OpenCost on {platform.toUpperCase()}.
                </Typography>
                < a
                    href={
                      platform === 'eks'
                        ? (process.env.REACT_APP_EKS_CLUSTER_ACCESS_OPENCOST_INSTALLATION_GUIDE || '#')
                        : (process.env.REACT_APP_AKS_CLUSTER_ACCESS_OPENCOST_INSTALLATION_GUIDE || '#')
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: '#185fa5',
                      textDecoration: 'none',
                    }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >
                    Download {platform.toUpperCase()} Installation Guide (PDF)
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#185fa5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                </a>
              </Box>

           </Box>

          </DialogContent>

          <Divider />

          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button
              onClick={() => setIsOpenCostModalOpen(false)}
              size="small"
              variant="outlined"
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                borderColor: 'divider',
                color: 'text.secondary',
                '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveOpenCostUrl}
              disableElevation
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
              }}
            >
              Save URL
            </Button>
          </DialogActions>
        </Dialog>

        {snackbarStatus && (
          <SnackbarNotification
            initialOpen={true}
            duration={5000}
            message={snackbarMessage}
            actionButtonName="Ok"
            theme={snackbarStatus === "success" ? SNACKBAR_THEME.GREEN : SNACKBAR_THEME.RED}
            yPosition="top"
            xPosition="center"
          />
        )}

        {showDeleteOpenCostConfirm && (
          <ConfirmModal
            open={true}
            headingLabel="Remove OpenCost URL"
            message={[
              <Typography variant="body1" component="div" key="1">Are you sure you want to remove the OpenCost URL configuration for</Typography>,
              <Typography variant="h6" color="error" component="div" key="2">
                {`"${deleteOpenCostRow?.clusterName || "this cluster"}"`}
              </Typography>,
            ]}
            continueBtnLabel="Yes, Remove"
            cancelBtnLabel="Cancel"
            onActionTaken={handleConfirmDeleteOpenCostUrl}
          />
        )}
      </Box>
    </ThemeProvider>
  );
};

export default ConnectK8sCluster;